import React, { useCallback, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsSharedProjectRoute } from '@client/hooks/use-router.hooks';
import { useSelectedProposalId } from '@client/hooks/use-selected-proposal-id.hooks';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { useSelectedElementId } from '@client/store/ui';
import { Design } from '@features/design';
import { useDesignCommentsUnread } from '@features/design/components/use-design-comments-unread.hook';
import { ELEMENT_LIST_HEADER_HEIGHT } from '@features/proposals/ElementListRow/element-list.constants';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { ActionsList } from '@features/proposals/ActionsList';
import { hasElementSelection } from '@features/proposals/helpers/proposal-selection.helpers';
import { useActionsForProposal } from '@features/proposals/hooks/use-actions-for-proposal.hooks';
import { useProposalActionsRequestCount } from '@features/proposals/hooks/use-proposal-actions-request.hooks';
import { IconButton } from '@ui/buttons';
import { PillTabs } from '@ui/PillTabs';
import { Tooltip } from '@ui/Tooltip';
import { PanelTabSlide, usePanelTabSlideDirection } from '@ui/layout';
import { ProjectSidePanel } from '../../../SidePanel/ProjectSidePanel';
import {
  buildPanelTabOrder,
  nextBuildPanelTab,
  panelTabs,
  resolveSharedBuildPanelTab,
  type BuildRightPanelTab,
  type RightPanelTab,
} from './build-right-panel-tab.helpers';

type RightPanelTabHandler = (tab: RightPanelTab) => void;
const COLLAPSED_TAB_VALUE: readonly RightPanelTab[] = [];

const useStyles = makeStyles()(({ palette }) => ({
  root: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabs: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    boxSizing: 'border-box',
    height: ELEMENT_LIST_HEADER_HEIGHT,
    paddingLeft: 8,
    backgroundColor: palette.warmSurface.main,
  },
  commentsButton: {
    display: 'flex',
    alignItems: 'center',
    height: 22,
    marginLeft: 'auto',
    marginRight: 8,
    borderRadius: 5,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
    '& .MuiIconButton-root': {
      width: 26,
      height: 22,
      padding: '0 2px',
      borderRadius: 5,
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  commentsButtonActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    color: 'rgba(0, 0, 0, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
    '& .MuiIconButton-root, & .MuiIconButton-root:hover': {
      color: 'inherit',
    },
  },
  commentsButtonLabel: {
    paddingRight: 8,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    color: 'inherit',
    '&.MuiTypography-root': {
      margin: 0,
    },
  },
}));

const renderBody = (
  tab: BuildRightPanelTab,
  proposalId: string | undefined,
) => {
  switch (tab) {
    case 'properties':
      return <ProjectSidePanel hideProposalActions />;
    case 'actions':
      if (!proposalId) return null;
      return <ActionsList proposalId={proposalId} />;
    case 'agent':
      return <Design.AiChatTest placement="panel" scope="elements" />;
    case 'comments':
      return <Design.Comments />;
    default:
      return assertUnreachable(tab);
  }
};

type BuildRightPanelProps = {
  /** An SBEF group row is the list selection, so element properties are not available. */
  groupSelected: boolean;
};

export const BuildRightPanel: React.FC<BuildRightPanelProps> = ({
  groupSelected,
}) => {
  const { classes, cx } = useStyles();
  const isSharedProject = useIsSharedProjectRoute();
  const [tab, setTab] = useState<BuildRightPanelTab>('properties');
  const selectedElementId = useSelectedElementId();
  const selectedProposalId = useSelectedProposalId();
  const proposalHasActions =
    useActionsForProposal(selectedProposalId).length > 0;
  const selectionKey = `${selectedElementId ?? ''}|${selectedProposalId ?? ''}`;
  const previousSelectionKey = usePrevious(selectionKey);
  const previousProposalId = usePrevious(selectedProposalId, {
    setInitial: true,
  });
  const previousElementId = usePrevious(selectedElementId, {
    setInitial: true,
  });
  const elementSelected = hasElementSelection(selectedElementId);
  const elementCleared =
    hasElementSelection(previousElementId) && !elementSelected;
  const baselineSelected = selectedProposalId === undefined;
  const showProperties =
    !groupSelected && (elementSelected || baselineSelected);
  const selectionChanged =
    previousSelectionKey !== undefined && previousSelectionKey !== selectionKey;
  const proposalSelected =
    previousProposalId !== selectedProposalId && Boolean(selectedProposalId);
  const baselineChosen =
    previousProposalId !== undefined && selectedProposalId === undefined;
  const actionsRequestCount = useProposalActionsRequestCount();
  const previousActionsRequestCount = usePrevious(actionsRequestCount, {
    setInitial: true,
  });
  const nextTab = nextBuildPanelTab(tab, {
    selectedProposalId,
    proposalHasActions,
    elementSelected,
    elementCleared,
    groupSelected,
    selectionChanged,
    proposalSelected,
    baselineChosen,
    actionsRequested: previousActionsRequestCount !== actionsRequestCount,
  });
  const resolvedTab = resolveSharedBuildPanelTab(nextTab, {
    isSharedProject,
    showProperties,
    proposalId: selectedProposalId,
  });
  if (resolvedTab !== 'empty' && resolvedTab !== tab) setTab(resolvedTab);
  const tabs = panelTabs(showProperties, Boolean(selectedProposalId)).filter(
    (name) => !(isSharedProject && name === 'agent'),
  );
  const panelFills = resolvedTab === 'agent' || resolvedTab === 'comments';
  const slideDirection = usePanelTabSlideDirection(
    buildPanelTabOrder(showProperties, Boolean(selectedProposalId)),
    resolvedTab,
  );
  const hasUnreadComments = useDesignCommentsUnread(tab === 'comments');

  const onTabChange = useCallback<RightPanelTabHandler>((next) => {
    setTab(next);
  }, []);

  const onCommentsClick = useCallback(() => {
    setTab('comments');
  }, []);

  return (
    <Box className={classes.root}>
      <Box className={classes.tabs}>
        <PillTabs
          tabs={tabs}
          value={
            resolvedTab === 'comments' || resolvedTab === 'empty'
              ? COLLAPSED_TAB_VALUE
              : resolvedTab
          }
          onChange={onTabChange}
        />
        {!isSharedProject && (
          <Box
            className={cx(
              classes.commentsButton,
              resolvedTab === 'comments' && classes.commentsButtonActive,
            )}
          >
            <Tooltip
              title={hasUnreadComments ? 'New activities' : 'History'}
              placement="bottom"
            >
              <IconButton
                category="ui"
                icon={hasUnreadComments ? 'commentUnread' : 'comment'}
                aria-label="History"
                onClick={onCommentsClick}
              />
            </Tooltip>
            {resolvedTab === 'comments' && (
              <Typography
                className={classes.commentsButtonLabel}
                component="span"
                onClick={onCommentsClick}
              >
                History
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <PanelTabSlide
        slideKey={resolvedTab}
        fill={panelFills}
        direction={slideDirection}
      >
        {resolvedTab === 'empty'
          ? null
          : renderBody(resolvedTab, selectedProposalId)}
      </PanelTabSlide>
    </Box>
  );
};
